import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play } from "lucide-react";

/**
 * YouTubePlayer - embeds a YouTube video and overlays a custom thumbnail
 * any time the video is paused or ended, blocking YouTube's "up next" suggestions.
 *
 * Props:
 *   - videoId (string, required)
 *   - thumbnail (string, optional) - custom poster image URL; otherwise uses the YT thumbnail.
 *   - title (string, optional)
 */
const YT_API_SRC = "https://www.youtube.com/iframe_api";
let ytApiPromise = null;

function loadYouTubeApi() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const tag = document.createElement("script");
    tag.src = YT_API_SRC;
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve(window.YT);
    };
  });
  return ytApiPromise;
}

const YouTubePlayer = ({ videoId, thumbnail, title }) => {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const [overlay, setOverlay] = useState(true); // shown initially & when paused/ended
  const [ready, setReady] = useState(false);

  const poster = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !mountRef.current) return;
      playerRef.current = new YT.Player(mountRef.current, {
        videoId,
        host: "https://www.youtube-nocookie.com",
        playerVars: {
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          fs: 1,
          playsinline: 1,
          controls: 1,
          disablekb: 0,
          showinfo: 0,
        },
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => {
            // 0 = ended, 1 = playing, 2 = paused, 3 = buffering, 5 = cued
            if (e.data === 1) setOverlay(false);
            else if (e.data === 0 || e.data === 2) setOverlay(true);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch (_) {
        /* noop */
      }
    };
  }, [videoId]);

  const handlePlay = useCallback(() => {
    if (!playerRef.current || !ready) return;
    try {
      // If video ended, seek to 0 to replay from the beginning.
      const state = playerRef.current.getPlayerState?.();
      if (state === 0) playerRef.current.seekTo(0, true);
      playerRef.current.unMute?.();
      playerRef.current.playVideo();
      setOverlay(false);
    } catch (_e) {
      /* noop */
    }
  }, [ready]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: "#000",
        aspectRatio: "16/9",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 30px 70px -20px rgba(0,0,0,0.45)",
      }}
    >
      <div
        ref={mountRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
      {overlay && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={title ? `Reproducir ${title}` : "Reproducir video"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            cursor: "pointer",
            backgroundImage: `url(${poster})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <span
            style={{
              width: 92,
              height: 92,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.95)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              transition: "transform 200ms ease",
            }}
          >
            <Play size={36} color="#1A1352" fill="#1A1352" />
          </span>
        </button>
      )}
    </div>
  );
};

export default YouTubePlayer;
